/**
 * 「消费」——本月还能花多少，钱都去哪了。
 *
 * 焦点是**剩余额度**，不是已花：一个高中生真正要做的决定是「今天还能不能点外卖」，
 * 而不是回顾自己花了多少。所以最大的数字给「剩余」，已花退成进度条。
 *
 * 数据由 Mac 单向推送上来，这里只读。所有查询在服务端已限定到王嘉晨本人。
 */

import { useCallback, useEffect, useState } from 'react'
import { moneyApi, yuan, yuanShort, type Category, type Summary, type TrendMonth, type Txn } from '../lib/money'

const CAT_EMOJI: Record<string, string> = {
  餐饮: '🍜', 外卖: '🛵', 便利店: '🏪', 交通: '🚇', 购物: '🛍️',
  服饰: '👕', 美妆: '💄', 转账: '💸', 退款: '↩️', 调整: '⚖️', 其他: '•',
}

/**
 * 打开这一页就把消费标记为已看，并把角标落到服务端算出来的新值。
 * 角标和通知横幅是两套东西：清角标不会关掉通知中心里那条，所以这里只管角标，
 * 横幅由 collapseKey 自然替换。
 */
function useMarkSeen() {
  useEffect(() => {
    void moneyApi.seen().then((r) => {
      if (!r.ok) return
      const nav = navigator as Navigator & {
        setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void>
      }
      // 不支持角标的浏览器直接跳过，别让它抛出来打断渲染
      const n = r.data.badge
      void (n > 0 ? nav.setAppBadge?.(n) : nav.clearAppBadge?.())?.catch(() => {})
    })
  }, [])
}

export default function Money() {
  useMarkSeen()
  const [sum, setSum] = useState<Summary | null>(null)
  const [cats, setCats] = useState<Category[]>([])
  const [txns, setTxns] = useState<Txn[]>([])
  const [trend, setTrend] = useState<TrendMonth[]>([])
  const [err, setErr] = useState<string | null>(null)

  const load = useCallback(async () => {
    const [s, c, t, tr] = await Promise.all([
      moneyApi.summary(), moneyApi.categories(), moneyApi.transactions(30), moneyApi.trend(6),
    ])
    if (s.ok) setSum(s.data); else setErr(s.message)
    if (c.ok) setCats(c.data.categories)
    if (t.ok) setTxns(t.data.transactions)
    if (tr.ok) setTrend(tr.data.months)
  }, [])
  useEffect(() => { void load() }, [load])

  if (err) return <Center>读不到消费数据：{err}</Center>
  if (!sum) return <Center>正在读取…</Center>

  const over = sum.remaining < 0
  const tight = !over && sum.usagePct >= 85

  return (
    <div className="safe-b" style={{ display: 'grid', gap: 16, padding: '10px 18px 32px', maxWidth: 540, margin: '0 auto', width: '100%' }}>

      {/* 主角：还能花多少 */}
      <section className="rise rise-1" style={{
        ...card, padding: '24px 22px 22px',
        background: over ? 'linear-gradient(160deg, var(--cal-missed-bg), var(--ps-surface) 60%)'
          : tight ? 'linear-gradient(160deg, var(--ps-accent-soft), var(--ps-surface) 62%)'
          : 'var(--ps-surface)',
        border: `1px solid ${over ? 'var(--cal-missed-line)' : tight ? 'var(--ps-accent-line)' : 'var(--ps-line)'}`,
      }}>
        <div style={{ fontSize: 13, letterSpacing: '.1em', color: 'var(--ps-text3)', fontWeight: 700 }}>
          {over ? '本月已超支' : '本月还能花'}
        </div>
        <div className="num" style={{
          fontSize: 'clamp(52px, 17vw, 76px)', fontWeight: 900, lineHeight: 1, letterSpacing: '-.045em',
          marginTop: 8, color: over ? 'var(--cal-missed-fg)' : 'var(--ps-text)',
        }}>{yuanShort(Math.abs(sum.remaining))}</div>

        <div style={{ height: 12, borderRadius: 99, background: 'var(--ps-line)', overflow: 'hidden', marginTop: 18 }}>
          <div style={{
            height: '100%', width: `${Math.min(100, sum.usagePct)}%`, borderRadius: 99,
            background: over ? 'var(--cal-missed-fg)' : 'var(--ps-accent)',
            transition: 'width .7s cubic-bezier(.2,.8,.3,1)',
          }} />
        </div>
        <div style={{ display: 'flex', marginTop: 10, fontSize: 14, color: 'var(--ps-text2)' }}>
          <span className="num">已花 {yuan(sum.total)}</span>
          <span className="num" style={{ marginLeft: 'auto' }}>额度 {yuanShort(sum.quota)}</span>
        </div>
      </section>

      {/* 两个决策数字 */}
      <div className="rise rise-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Stat label="还剩" value={`${sum.daysLeft}`} unit="天" />
        <Stat label="日均可用" value={sum.dailyBudget !== null ? yuanShort(sum.dailyBudget) : '—'}
          tone={sum.dailyBudget !== null && sum.dailyBudget < 20 ? 'warn' : 'normal'} />
      </div>

      {/* 钱去哪了 */}
      {cats.length > 0 && (
        <section className="rise rise-3" style={{ ...card, padding: '20px 20px 18px' }}>
          <H>钱花在哪</H>
          <div style={{ display: 'grid', gap: 14, marginTop: 16 }}>
            {cats.filter((c) => c.total > 0).slice(0, 6).map((c) => {
              const max = Math.max(...cats.filter((x) => x.total > 0).map((x) => x.total))
              return (
                <div key={c.category}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 7 }}>
                    <span style={{ fontSize: 17 }}>{CAT_EMOJI[c.category] ?? '•'}</span>
                    <span style={{ fontSize: 16, fontWeight: 600 }}>{c.category}</span>
                    <span className="num" style={{ fontSize: 13, color: 'var(--ps-text3)' }}>{c.count} 笔</span>
                    <span className="num" style={{ marginLeft: 'auto', fontSize: 17, fontWeight: 800 }}>{yuanShort(c.total)}</span>
                  </div>
                  <div style={{ height: 8, borderRadius: 99, background: 'var(--ps-line)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${(c.total / max) * 100}%`, borderRadius: 99, background: 'var(--ps-tint)' }} />
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* 月度走势 */}
      {trend.length > 1 && (
        <section className="rise rise-4" style={{ ...card, padding: '20px 20px 18px' }}>
          <H>最近几个月</H>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 116, marginTop: 18 }}>
            {trend.map((m) => {
              const max = Math.max(...trend.map((x) => x.total), sum.quota)
              const h = Math.max(6, (m.total / max) * 88)
              const isNow = m.month === sum.month
              return (
                <div key={m.month} style={{ flex: 1, display: 'grid', justifyItems: 'center', gap: 7 }}>
                  <span className="num" style={{ fontSize: 12, color: isNow ? 'var(--ps-accent)' : 'var(--ps-text3)', fontWeight: isNow ? 800 : 500 }}>
                    {yuanShort(m.total).replace('¥', '')}
                  </span>
                  <div style={{
                    width: '100%', height: h, borderRadius: 8,
                    background: isNow ? 'var(--ps-accent)' : 'var(--ps-line)',
                  }} />
                  <span className="num" style={{ fontSize: 12, color: 'var(--ps-text3)' }}>{m.month.slice(5)}月</span>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* 最近消费 */}
      {txns.length > 0 && (
        <section className="rise rise-4" style={{ ...card, padding: '20px 8px 10px 20px' }}>
          <H>最近消费</H>
          <div style={{ display: 'grid', marginTop: 8 }}>
            {txns.slice(0, 12).map((t) => (
              <div key={t.id} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '13px 12px 13px 0',
                borderBottom: '1px solid var(--ps-line-soft)',
              }}>
                <span style={{ fontSize: 19, width: 26, textAlign: 'center' }}>{CAT_EMOJI[t.category] ?? '•'}</span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 15.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.scene || t.category}
                  </div>
                  <div className="num" style={{ fontSize: 12.5, color: 'var(--ps-text3)', marginTop: 3 }}>
                    {t.transaction_time.slice(5, 16)}
                  </div>
                </div>
                <span className="num" style={{
                  fontSize: 17, fontWeight: 800,
                  color: t.amount < 0 ? 'var(--ps-accent)' : 'var(--ps-text)',
                }}>{t.amount < 0 ? '+' : ''}{yuanShort(Math.abs(t.amount))}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

function H({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 13, letterSpacing: '.1em', color: 'var(--ps-text3)', fontWeight: 700 }}>{children}</div>
}
function Stat({ label, value, unit, tone }: { label: string; value: string; unit?: string; tone?: 'warn' | 'normal' }) {
  return (
    <div style={{ ...card, padding: '16px 18px' }}>
      <div style={{ fontSize: 13, color: 'var(--ps-text3)', fontWeight: 600 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginTop: 6 }}>
        <span className="num" style={{
          fontSize: 30, fontWeight: 900, letterSpacing: '-.03em',
          color: tone === 'warn' ? 'var(--cal-missed-fg)' : 'var(--ps-text)',
        }}>{value}</span>
        {unit && <span style={{ fontSize: 14, color: 'var(--ps-text3)' }}>{unit}</span>}
      </div>
    </div>
  )
}
function Center({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: 48, textAlign: 'center', color: 'var(--ps-text3)', fontSize: 15 }}>{children}</div>
}
const card: React.CSSProperties = {
  background: 'var(--ps-surface)', borderRadius: 22,
  border: '1px solid var(--ps-line)', boxShadow: 'var(--ps-shadow-sm)',
}
